import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import InfoIcon from '@mui/icons-material/Info'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Paper from '@mui/material/Paper'
import Snackbar from '@mui/material/Snackbar'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { useQuery } from '@tanstack/react-query'
import KNCaption from 'components/KNCaption/KNCaption'
import KNDialog from 'components/KNDialog/KNDialog'
import KNLoader from 'components/KNLoader/KNLoader'
import { type ReactElement, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import TripAcceptance from 'screens/TripDashboard/TripAcceptance'
import TripCargo from 'screens/TripDashboard/TripCargo'
import type { TripData } from 'screens/TripDashboard/TripDashboard.types'
import TripHeader from 'screens/TripDashboard/TripHeader'
import LegHeader from 'screens/TripDetails/LegHeader'
import { tripLegsQueryOptions } from 'screens/TripDetails/TripDetails.service'
import UitReferenceAttribute from 'screens/TripDetails/UitReferenceAttribute'
import QuickStatusUpdateStop from './QuickStatusUpdateStop'

interface QuickStatusUpdateDialogPayload {
  trip: TripData
}

interface QuickStatusUpdateDialogProps {
  payload: QuickStatusUpdateDialogPayload
  open: boolean
  onAction: (updatedTrip: TripData, action?: string) => void
  onClose: () => void
}

const QuickStatusUpdateDialog = ({ payload, open, onAction, onClose }: QuickStatusUpdateDialogProps): ReactElement => {
  const theme = useTheme()
  const { t } = useTranslation()
  const legsQuery = useQuery({
    ...tripLegsQueryOptions(payload.trip.entityId),
    enabled: open,
  })
  const legsData = legsQuery.data ?? []
  const [acceptanceSnackbar, setAcceptanceSnackbar] = useState(false)
  const [acceptanceAction, setAcceptanceAction] = useState('')
  const [expanded, setExpanded] = useState<string | null>(null)

  const handlePanelChange = (panel: string): void => {
    setExpanded(panel === expanded ? null : panel)
  }

  const handleTripAcceptanceAction = useCallback((updatedTrip: TripData, action: string) => {
    setAcceptanceAction(action)
    setAcceptanceSnackbar(true)
    onAction(updatedTrip, action)
  }, [onAction])

  const handleAcceptanceClose = () => {
    setAcceptanceSnackbar(false)
  }

  useEffect(() => {
    if (legsData) {
      let defaultPanel: string | null = null
      // find default panel to expand
      legsData.forEach((leg) => {
        leg.wayPoints.forEach((stop) => {
          // ignore if there's something chosen already
          if (defaultPanel) {
            return
          }
          if (stop.availableStatuses.length > 0 && stop.type !== 'CUS') {
            defaultPanel = stop.wayPointCid
          }
        })
      })
      setExpanded(defaultPanel)
    }
  }, [legsData])

  return (
    <>
      <KNDialog
        open={open}
        closeAction={false}
        onClose={onClose}
        allowBackdropClose
        maxWidth="xs"
        slideDirection="left"
        sx={{
          '& .MuiDialog-container': {
            xs: {
              '& > .MuiPaper-root': {
                backgroundColor: theme.palette.darkblue[10],
              },
            },
            md: {
              justifyContent: 'flex-end',
              '& > .MuiPaper-root': {
                margin: '0.5rem',
                height: 'calc(100vh - 1rem)',
                maxHeight: '100vh',
              },
            },
          },
        }}
      >
        {legsQuery.isPending && (
          <KNLoader>
            <Typography>{t('screens.cs.trip_details.loading')}</Typography>
          </KNLoader>
        )}
        {legsQuery.isSuccess && (
          <Stack spacing={1} m={-1}>
            <TripAcceptance trip={payload.trip} onChange={handleTripAcceptanceAction} />

            <Paper sx={{ px: 2, py: 1.5 }}>
              <Stack spacing={1}>
                <TripHeader trip={payload.trip} stacked size="large" />
                <TripCargo trip={payload.trip} />
              </Stack>
            </Paper>

            {legsData.map((leg) => (
              <Paper key={leg.shipmentNumber}>
                <Stack divider={<Divider orientation="horizontal" flexItem sx={{ borderColor: 'primary.lightest' }} />}>
                  <Box px={2} py={1.5}>
                    <LegHeader trip={payload.trip} leg={leg} size="large" stacked />
                    <UitReferenceAttribute leg={leg} />
                  </Box>
                  {leg.wayPoints.map((stop) => (
                    <QuickStatusUpdateStop
                      key={stop.wayPointCid}
                      trip={payload.trip}
                      leg={leg}
                      stop={stop}
                      expanded={expanded === stop.wayPointCid}
                      onChange={handlePanelChange}
                    />
                  ))}
                </Stack>
              </Paper>
            ))}

            <KNCaption icon={<InfoIcon />}>
              {t('screens.cs.trip_details.local_time')}
            </KNCaption>
          </Stack>
        )}
      </KNDialog>
      <Snackbar
        open={acceptanceSnackbar}
        onClose={handleAcceptanceClose}
      >
        <Alert
          onClose={handleAcceptanceClose}
          severity={acceptanceAction === 'trip_accepted' ? 'success' : 'error'}
          variant="filled"
          sx={{ width: '100%', color: theme.palette.success.contrastText }}
          iconMapping={{
            success: <CheckCircleOutlineIcon fontSize="inherit" />,
          }}
        >
          {acceptanceAction === 'trip_accepted' ?
            <Typography color={theme.palette.success.contrastText}>
              {t('screens.cs.trip_details.accept_trip.trip_accepted_snackbar', {
                tripNumber: payload.trip.tripNumber,
              })}
            </Typography> :
            <Typography>
              {t('screens.cs.trip_details.accept_trip.trip_declined_snackbar', {
                tripNumber: payload.trip.tripNumber,
              })}
            </Typography>
          }
        </Alert>
      </Snackbar>
    </>
  )
}

export default QuickStatusUpdateDialog
